import CustomTooltip from '../../../shared/CustomTooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    Chip,
    FormControlLabel,
    Grid,
    Skeleton,
    Switch,
    Typography,
    useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as z from 'zod';
import {
    useGetAllAssetsQuery,
    useGetUserAccessControlQuery,
    useSetUserAccessControlMutation,
} from '../../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import UserRole from '../../../../shared/AuthRoles';
import FormMultiSelectWithALL from '../../../shared/FormMultiSelectWithALL';
import toast from '../../../shared/Toast';

const UserAccessControlPricing = () => {
    const loggedInUser = UserRole();
    const navigate = useNavigate();
    const [experiencesOpen, setExperiencesOpen] = useState(true);
    const { data: employeeAccess, isLoading } = useGetUserAccessControlQuery(
        `/guests/${loggedInUser?.company}/employee-access`,
        { skip: loggedInUser.role !== 'guest_admin' },
    );

    const theme = useTheme();

    const { data: getAvailableModules, isFetching } = useGetAllAssetsQuery(
        { url: `/modules` },
        { skip: loggedInUser.role !== 'guest_admin' },
    );

    const [saveEmployeeAccess] = useSetUserAccessControlMutation();
    const schema = z
        .object({
            hidePrices: z.coerce.boolean().default(false),
            hidePricesModules: z
                .array(z.object({ value: z.number(), label: z.string() }))
                .optional(),
            hidePricesRoles: z
                .array(z.object({ label: z.string(), value: z.string() }))
                .optional(),
        })
        .refine(
            (data) => {
                if (!data.hidePrices) return true;
                if (experiencesOpen) {
                    return data.hidePricesRoles?.length ?? 0;
                } else {
                    return (
                        (data.hidePricesRoles?.length ?? 0) > 0 &&
                        (data.hidePricesModules?.length ?? 0)
                    );
                }
            },
            {
                message:
                    'Please select at least one experience and one role when hiding prices.',
                path: ['hidePrices'],
            },
        );

    const { reset, control, setValue, handleSubmit, formState } =
        useForm<EmployeeAccessControl>({
            resolver: zodResolver(schema),
        });

    const hidePrices = useWatch({
        control,
        name: 'hidePrices',
        defaultValue: false,
    });
    const selectedModules = useWatch({
        control,
        name: 'hidePricesModules',
        defaultValue: [],
    });
    const selectedRoles = useWatch({ control, name: 'hidePricesRoles' });

    useEffect(() => {
        if (loggedInUser.role !== 'guest_admin') {
            navigate(`/permissionDenied`);
        }
    }, []);

    useEffect(() => {
        if (employeeAccess && getAvailableModules) {
            const realModules = getAvailableModules.filter(
                (m) => m.isAvailable,
            );

            const hidePricesModules = employeeAccess.hidePricesModules?.map(
                (moduleFromAPI: any) => {
                    const matching = realModules.find(
                        (m) => m.id === moduleFromAPI.id,
                    );
                    return {
                        value: moduleFromAPI.id,
                        label: matching
                            ? `${matching.name} (${matching.type}) hosted by ${matching.owner.name}`
                            : (moduleFromAPI.name ??
                              `Module ${moduleFromAPI.id}`),
                    };
                },
            );

            const { hidePrices, hidePricesRoles } = employeeAccess;

            reset({
                hidePrices,
                hidePricesModules,
                hidePricesRoles: hidePricesRoles?.map((role: string) => {
                    const match = allRoles.find((r) => r.value === role);
                    return match || { value: role, label: role };
                }),
            });

            if (employeeAccess?.hidePricesModules.length > 0) {
                setExperiencesOpen(false);
            } else {
                setExperiencesOpen(true);
            }
        }
    }, [employeeAccess, getAvailableModules]);

    type EmployeeAccessControl = z.infer<typeof schema>;
    type RoleOption = { label: string; value: string };

    const onSubmit = async (data: EmployeeAccessControl) => {
        const payload = {
            ...data,
            hidePricesModules: experiencesOpen
                ? []
                : data.hidePricesModules?.map((m) => ({
                      id: m.value,
                  })),
            hidePricesRoles: data.hidePricesRoles?.map((r) => r.value),
        };

        try {
            await saveEmployeeAccess([loggedInUser?.company, payload]).unwrap();
            toast.success('Successfully saved employee access information');
        } catch (error: any) {
            toast.error(
                'Failed to save employee access info. Please try again.',
            );
            console.error('Employee access error:', error);
        }
    };

    const handleCheck = () => {
        const newValue = !hidePrices;
        setValue('hidePrices', newValue);

        if (!newValue) {
            // When hidePrices is checked off we clear other fields
            setValue('hidePricesModules', []);
            setValue('hidePricesRoles', []);
        }
    };

    const handleExperiences = () => {
        if (experiencesOpen) setValue('hidePricesModules', []);

        setExperiencesOpen(!experiencesOpen);
    };

    const allRoles = [
        { value: 'company_admin', label: 'Company Admin' },
        { value: 'team_admin', label: 'Team Admin' },
        { value: 'user', label: 'User' },
        { value: 'view_only_user', label: 'View Only User' },
    ];

    const allRoleOptions: any = [
        { value: 'ALL', label: 'All' },
        {
            value: 'company_admin',
            label: 'Company Admin',
        },
        {
            value: 'team_admin',
            label: 'Team Admin',
        },
        { value: 'user', label: 'User' },
        {
            value: 'view_only_user',
            label: 'View Only User',
        },
    ];

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Grid
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '90px',
                }}
            >
                <Grid
                    sx={{
                        width: '100%',
                    }}
                >
                    <Typography variant='h1'>Pricing Access Control</Typography>
                    <Typography
                        sx={{
                            fontSize: '12px',
                            paddingTop: '10px',
                        }}
                    >
                        Control which user roles are allowed to view prices on
                        listings. Once saved, the users under that role will not
                        see any pricing for the experience you selected.
                    </Typography>
                </Grid>

                <Grid container spacing={2}>
                    <Grid item container columns={25} spacing={1}>
                        <Grid
                            item
                            xs={12}
                            lg={5}
                            sx={{ display: 'flex', gap: '2px' }}
                        >
                            <Typography color='text.primary'>
                                Hide Price
                            </Typography>
                        </Grid>
                        <Grid item xs={25} lg={9}>
                            {isLoading || isFetching ? (
                                <Skeleton
                                    animation='wave'
                                    width={20}
                                    height={20}
                                />
                            ) : (
                                <>
                                    <FormControlLabel
                                        label=''
                                        control={
                                            <Switch
                                                inputProps={{
                                                    'aria-label': 'Hide price',
                                                }}
                                            />
                                        }
                                        checked={hidePrices}
                                        onChange={handleCheck}
                                        sx={{ mr: 1 }}
                                    />

                                    {formState.errors.hidePrices && (
                                        <Typography
                                            color='error'
                                            fontSize='12px'
                                        >
                                            {
                                                formState.errors.hidePrices
                                                    .message
                                            }
                                        </Typography>
                                    )}
                                </>
                            )}
                        </Grid>
                    </Grid>
                    <Grid item container columns={25} spacing={1}>
                        <Grid
                            item
                            xs={12}
                            lg={5}
                            sx={{ display: 'flex', gap: '2px' }}
                        >
                            <Typography
                                id='pricing-access-experiences-label'
                                color='text.primary'
                            >
                                Experiences
                            </Typography>
                            <CustomTooltip
                                title={
                                    <ul>
                                        <Typography>
                                            <li>
                                                {' '}
                                                By selecting 'Appy to all
                                                current and future experiences'
                                                the price will be hidded for all
                                                current listings on 7DAY as well
                                                as any new ones that are added
                                                to the system
                                            </li>
                                            <li>
                                                {' '}
                                                By untoggling, you will need to
                                                select at least one experience
                                                from the list. If you select
                                                'All current listings'. This
                                                will apply to all the current
                                                listings on 7DAY but exclude any
                                                new ones that are added to the
                                                system.
                                            </li>
                                        </Typography>
                                    </ul>
                                }
                                placement='right'
                            >
                                <HelpOutlineIcon
                                    sx={{
                                        color: 'text.primary',
                                        fontSize: '16px',
                                    }}
                                />
                            </CustomTooltip>
                        </Grid>
                        <Grid item xs={25} lg={9}>
                            {isLoading || isFetching ? (
                                <>
                                    {' '}
                                    <Skeleton
                                        animation='wave'
                                        width={400}
                                        height={20}
                                    />
                                    <Skeleton
                                        animation='wave'
                                        width={400}
                                        height={20}
                                    />
                                    <Skeleton
                                        animation='wave'
                                        width={400}
                                        height={20}
                                    />
                                </>
                            ) : (
                                <FormControlLabel
                                    label={
                                        <Typography color='text.primary'>
                                            Apply to all current & furture
                                            experiences
                                        </Typography>
                                    }
                                    control={<Switch />}
                                    checked={experiencesOpen}
                                    onChange={handleExperiences}
                                    sx={{ mr: 1 }}
                                    disabled={!hidePrices}
                                />
                            )}
                        </Grid>
                    </Grid>

                    {experiencesOpen === false && (
                        <Grid item container columns={25} spacing={1}>
                            <Grid
                                item
                                xs={12}
                                lg={5}
                                sx={{ display: 'flex', gap: '2px' }}
                            ></Grid>
                            <Grid item xs={25} lg={9}>
                                {isLoading || isFetching ? (
                                    <>
                                        {' '}
                                        <Skeleton
                                            animation='wave'
                                            width={400}
                                            height={20}
                                        />
                                        <Skeleton
                                            animation='wave'
                                            width={400}
                                            height={20}
                                        />
                                        <Skeleton
                                            animation='wave'
                                            width={400}
                                            height={20}
                                        />
                                    </>
                                ) : (
                                    <FormMultiSelectWithALL
                                        name='hidePricesModules'
                                        control={control}
                                        aria-labelledby='pricing-access-experiences-label'
                                        placeholder={
                                            selectedModules?.length === 0
                                                ? 'Select experiences'
                                                : ''
                                        }
                                        disabled={!hidePrices}
                                        options={
                                            getAvailableModules
                                                ? [
                                                      {
                                                          value: 'ALL',
                                                          label: 'All Current Listings',
                                                      },
                                                      ...getAvailableModules
                                                          .filter(
                                                              (module) =>
                                                                  module.isAvailable,
                                                          )
                                                          .map(
                                                              (
                                                                  module: any,
                                                              ) => ({
                                                                  value: module.id,
                                                                  label: `${module.name} (${module.type}) hosted by ${module.owner.name}`,
                                                              }),
                                                          ),
                                                  ]
                                                : []
                                        }
                                        isOptionEqualToValue={(
                                            option: any,
                                            value: any,
                                        ) =>
                                            typeof value === 'object'
                                                ? option.value === value.id
                                                : option.value === value
                                        }
                                        getOptionLabel={(option: any) =>
                                            typeof option === 'object' &&
                                            option !== null
                                                ? option.label || ''
                                                : ''
                                        }
                                        renderTags={(
                                            value: any[],
                                            getTagProps,
                                        ) => {
                                            const maxVisible = 3;
                                            return [
                                                ...value
                                                    .slice(0, maxVisible)
                                                    .map((option, index) => (
                                                        <Chip
                                                            {...getTagProps({
                                                                index,
                                                            })}
                                                            key={
                                                                option.value ||
                                                                index
                                                            }
                                                            label={option.label}
                                                            sx={{
                                                                fontSize:
                                                                    '13px',
                                                                color: theme
                                                                    .palette
                                                                    .midGrey
                                                                    .main,
                                                                backgroundColor:
                                                                    theme
                                                                        .palette
                                                                        .neutral
                                                                        .main,
                                                                height: 24,
                                                            }}
                                                        />
                                                    )),
                                                value.length > maxVisible ? (
                                                    <Chip
                                                        key='more'
                                                        label={`+${
                                                            value.length -
                                                            maxVisible
                                                        }`}
                                                        sx={{
                                                            fontSize: '13px',
                                                            color: theme.palette
                                                                .midGrey.main,
                                                            backgroundColor:
                                                                theme.palette
                                                                    .neutral
                                                                    .main,
                                                            height: 24,
                                                        }}
                                                    />
                                                ) : null,
                                            ];
                                        }}
                                        onChange={(_, selectedOptions) => {
                                            const selectedValues =
                                                selectedOptions.map(
                                                    (opt: any) => opt.value,
                                                );
                                            const isAllSelected =
                                                selectedValues.includes('ALL');

                                            const realModules =
                                                getAvailableModules?.filter(
                                                    (m) => m.isAvailable,
                                                ) || [];

                                            const allOptions = realModules.map(
                                                (m) => ({
                                                    value: m.id,
                                                    label: `${m.name} (${m.type}) hosted by ${m.owner.name}`,
                                                }),
                                            );

                                            if (isAllSelected) {
                                                const isAlreadyAllSelected =
                                                    selectedModules?.length ===
                                                        allOptions.length &&
                                                    allOptions.every((opt) =>
                                                        selectedModules?.some(
                                                            (sel: any) =>
                                                                sel.value ===
                                                                opt.value,
                                                        ),
                                                    );

                                                setValue(
                                                    'hidePricesModules',
                                                    isAlreadyAllSelected
                                                        ? []
                                                        : allOptions,
                                                );
                                            } else {
                                                const cleaned =
                                                    selectedOptions.filter(
                                                        (opt: any) =>
                                                            opt.value !== 'ALL',
                                                    );

                                                const shouldSelectAll =
                                                    cleaned.length ===
                                                    allOptions.length;

                                                setValue(
                                                    'hidePricesModules',
                                                    shouldSelectAll
                                                        ? allOptions
                                                        : cleaned,
                                                );
                                            }
                                        }}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    )}

                    <Grid item container columns={25} spacing={1}>
                        <Grid
                            item
                            xs={12}
                            lg={5}
                            sx={{ display: 'flex', gap: '2px' }}
                        >
                            <Typography
                                id='pricing-access-roles-label'
                                color='text.primary'
                            >
                                Apply to Roles
                            </Typography>
                        </Grid>
                        <Grid item xs={25} lg={9}>
                            {isLoading || isFetching ? (
                                <Skeleton
                                    animation='wave'
                                    width={400}
                                    height={20}
                                />
                            ) : (
                                <FormMultiSelectWithALL<RoleOption>
                                    name='hidePricesRoles'
                                    control={control}
                                    aria-labelledby='pricing-access-roles-label'
                                    options={allRoleOptions}
                                    disabled={!hidePrices}
                                    placeholder={
                                        selectedRoles?.length === 0
                                            ? 'Select roles'
                                            : ''
                                    }
                                    filterOptions={(options, state) =>
                                        options.filter((opt) =>
                                            state
                                                .getOptionLabel(opt)
                                                .toLowerCase()
                                                .includes(
                                                    state.inputValue.toLowerCase(),
                                                ),
                                        )
                                    }
                                    renderTags={(value: any[], getTagProps) => {
                                        const maxVisible = 3;
                                        return [
                                            ...value
                                                .slice(0, maxVisible)
                                                .map((option, index) => (
                                                    <Chip
                                                        {...getTagProps({
                                                            index,
                                                        })}
                                                        key={
                                                            option.value ||
                                                            index
                                                        }
                                                        label={option.label}
                                                        sx={{
                                                            fontSize: '13px',
                                                            backgroundColor:
                                                                theme.palette
                                                                    .faded.main,
                                                            height: 24,
                                                        }}
                                                    />
                                                )),
                                            value.length > maxVisible ? (
                                                <Chip
                                                    key='more'
                                                    label={`+${
                                                        value.length -
                                                        maxVisible
                                                    }`}
                                                    sx={{
                                                        fontSize: '13px',
                                                        backgroundColor:
                                                            theme.palette.faded
                                                                .main,
                                                        height: 24,
                                                    }}
                                                />
                                            ) : null,
                                        ];
                                    }}
                                    onChange={(_, selectedOptions) => {
                                        const isAllSelected =
                                            selectedOptions.some(
                                                (opt: any) =>
                                                    (opt as RoleOption)
                                                        ?.value === 'ALL',
                                            );

                                        if (isAllSelected) {
                                            setValue(
                                                'hidePricesRoles',
                                                allRoles,
                                            );
                                        } else {
                                            const cleaned: RoleOption[] = (
                                                selectedOptions || []
                                            )
                                                .filter(
                                                    (opt: any) =>
                                                        (opt as RoleOption)
                                                            ?.value !== 'ALL',
                                                )
                                                .map((opt: any) =>
                                                    typeof opt === 'string'
                                                        ? allRoles.find(
                                                              (r) =>
                                                                  r.value ===
                                                                  opt,
                                                          ) || {
                                                              value: opt,
                                                              label: opt,
                                                          }
                                                        : opt,
                                                );

                                            setValue(
                                                'hidePricesRoles',
                                                cleaned,
                                            );
                                        }
                                    }}
                                />
                            )}
                        </Grid>
                    </Grid>
                    <Grid item container columns={25} spacing={1}>
                        <Grid
                            item
                            xs={25}
                            lg={14}
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <CustomButton
                                type='submit'
                                variant='contained'
                                color='primary'
                                loading={formState.isSubmitting}
                                disabled={formState.isSubmitting}
                            >
                                Save
                            </CustomButton>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </form>
    );
};

export default UserAccessControlPricing;
